import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import {
  GlossaryItem,
  fetchObjects,
  createObject,
  updateObject,
  deleteObject,
  objectsStore,
  getCachedObjects,
  sectionDataObject
} from "@/lib/stores/objectsStore";

// Create context with glossary-specific interface including CRUD operations
interface GlossaryDataContextType {
  glossaryItems: ObjectItem[];
  isLoading: boolean;
  error: string | null;
  loadData: () => Promise<void>;
  createGlossary: (item: {
    game_id: string;
    name: string;
    type: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version: number;
    identifiers?: string[];
    folder?: string | null;
  }) => Promise<ObjectItem>;
  updateGlossary: (item: {
    id: string;
    game_id: string;
    name: string;
    type: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number;
    identifiers?: string[];
    folder?: string | null;
  }) => Promise<ObjectItem>;
  deleteGlossary: (id: string, gameId: string) => Promise<void>;
}

const GlossaryDataContext = createContext<GlossaryDataContextType | null>(null);

interface GlossaryDataProviderProps {
  children: React.ReactNode;
  type?: 'glossary' | 'document' | 'all'; // Add type parameter
}

// Separate component that only renders when authenticated and has a selected game
const AuthenticatedGlossaryDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuthStore();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const [glossaryItems, setGlossaryItems] = useState<ObjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!selectedGame?.id) return;

    setIsLoading(true);
    setError(null);
    try {
      // fetchObjects now returns cached data immediately if available
      const fetchedGlossaryItems = await fetchObjects(selectedGame.id, undefined, toast);
      setGlossaryItems(fetchedGlossaryItems);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load glossary items";
      setError(message);
      console.error("GlossaryDataProvider - Error loading glossary items:", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGame?.id, toast]);

  const createGlossary = async (item: {
    game_id: string;
    name: string;
    type: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version: number;
    identifiers?: string[];
    folder?: string | null;
  }) => {
    const newGlossaryItem = await createObject(item, toast);
    return newGlossaryItem;
  };

  const updateGlossary = async (item: {
    id: string;
    game_id: string;
    name: string;
    type: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number;
    identifiers?: string[];
    folder?: string | null;
  }) => {
    const updatedGlossaryItem = await updateObject(item, 'patch', undefined, toast);
    // The store is already updated with optimistic update, and our subscription will handle the state update
    return updatedGlossaryItem;
  };

  const deleteGlossary = async (id: string, gameId: string) => {
    await deleteObject(id, gameId, toast);
    // The store is already updated with optimistic update, and our subscription will handle the state update
    toast({
      title: "Success",
      description: "Object deleted successfully",
    });
  };

  // Load cached data immediately when game changes, then fetch fresh data
  useEffect(() => {
    if (!selectedGame?.id) return;

    // Load cached data immediately for instant response
    const cachedGlossaryItems = getCachedObjects(selectedGame.id);
    if (cachedGlossaryItems.length > 0) {
      setGlossaryItems(cachedGlossaryItems);
    }
    
    // Always fetch fresh data (which will be fast if cached, or do background sync)
    if (!isInitialized) {
      loadData();
      setIsInitialized(true);
    }
  }, [selectedGame?.id, isInitialized, loadData]);

  // Reset initialization when selected game changes
  useEffect(() => {
    setIsInitialized(false);
  }, [selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!selectedGame?.id) return;

    const unsubscribe = objectsStore.subscribe(() => {
      // Get the latest data from the store and filter by current game
      const allGlossaryItems = objectsStore.getData();
      const gameGlossaryItems = allGlossaryItems.filter(
        (item) => item.game_id === selectedGame.id && 
          (item.type === "glossary" || item.type === "Library Object" || item.type === "document")
      );
      setGlossaryItems(gameGlossaryItems);
    });

    return () => unsubscribe();
  }, [selectedGame?.id]);

  const value: GlossaryDataContextType = {
    glossaryItems,
    isLoading,
    error,
    loadData,
    createGlossary,
    updateGlossary,
    deleteGlossary,
  };

  return (
    <GlossaryDataContext.Provider value={value}>
      {children}
    </GlossaryDataContext.Provider>
  );
};

export const GlossaryDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <GlossaryDataContext.Provider value={null}>
        {children}
      </GlossaryDataContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return (
      <AuthenticatedGlossaryDataProvider>
        {children}
      </AuthenticatedGlossaryDataProvider>
    );
  }

  // If not authenticated, provide null context
  return (
    <GlossaryDataContext.Provider value={null}>
      {children}
    </GlossaryDataContext.Provider>
  );
};

export const useGlossaryDataContext = () => {
  const context = useContext(GlossaryDataContext);
  if (!context) {
    throw new Error(
      "useGlossaryDataContext must be used within GlossaryDataProvider and user must be authenticated"
    );
  }
  return context;
};

// Optional hook that returns null if not in context (for use in components that may be outside the provider)
export const useGlossaryDataContextOptional = () => {
  return useContext(GlossaryDataContext);
};



